import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export enum TestTypes {
  ThickeningTime = 1,
  FluidLoss = 2,
  FreeFluid = 3,
  Rheology = 4,
  HpHtRheology = 5,
  StaticGelStrength = 6,
  FoamStabilityCement = 7,
  FoamStabilitySlurry = 8,
  PressurizedMudScale = 10,
  Pycnometer = 11,
  ApiSedimentationInput = 12,
  Expansion = 13,
  ShearBond = 14,
  AcidSolubility = 15,
  BasicCrush = 16,
  SplittingTs = 17,
  UnconfinedCs = 18,
  TriaxilConfinedCs = 19,
  CompStrengthUca = 20,
  CompStrengthCrush = 21,
  CompStrengthFoamUca = 22,
  CompStrengthFoamCrush = 23,
  SpacerCompatibility = 24,
  SpacerQAQC = 25,
  SpacerSSST = 26,
  ContaminationThickeningTime = 27,
  ContaminationCompStrengthUca = 28,
  ContaminationCompStrengthCrush = 29,
  ContaminationCompStrengthFoamUca = 30,
  ContaminationCompStrengthFoamCrush = 31,
  SpacerHpHtRheology = 32,
  Mixability = 33,
  CompStrengthConditionedUCA = 34,
  ContaminationCompStrengthConditionedUCA = 35,
  CompStrengthFoamConditionedUCA = 36,
  ContaminationCompStrengthFoamConditionedUCA = 37,
  RheologyDownhole = 101,
  RheologySurface = 102,
  FluidLossStatic = 103,
  FluidLossStirred = 104,
  HpHtRheologyFan = 105,
  HpHtRheologyChann7600 = 106,
  StaticGelStrengthMacs = 107,
  StaticGelStrengthSgsa = 108,
  SpacerHpHtRheologyFan = 109,
  SpacerHpHtRheologyChann7600 = 110,
  HpHtRheologyChann7550 = 111,
  SpacerHpHtRheologyChann7550 = 112
}

export interface ComplianceTestOptionDto {
  testTypeId: number;
  testTypeName: string;
}

export interface RedesignReasonOptionDto {
  reasonTypeId: number;
  reasonTypeName: string;
  requiresComplianceTests: boolean;
  availableComplianceTests: ComplianceTestOptionDto[];
}

export interface SpacerRedesignReasonsResponseDto {
  reasons: RedesignReasonOptionDto[];
}

export interface UpdateSpacerRedesignReasonRequestDto {
  slurryId: number;
  isRedesign: boolean;
  reasonType?: number | null;
  comments?: string | null;
  parentSlurryId?: number | null;
  complianceTests?: number[];
}

export interface SlurryRedesignReasonDto {
  reasonTypeId: number;
  comments?: string | null;
  parentSlurryId?: number | null;
  complianceTestIds: number[];
}

/** Mirrors C# SlurryDto (header + parameters) returned by GET /slurry/{id}. */
export interface SpacerDetailDto {
  id: number;
  ufSlurryID: string;
  cementProjID: number;
  isParentProjectCompleted?: boolean;
  /** SlurryStatuses enum (1=Draft … 7=SentToLabAgain) */
  status: number;
  /** SlurryTypes enum */
  type: number;
  targetThickeningTimeFrMins?: number;
  targetThickeningTimeToMins?: number;
  measuredDepth?: number;
  jobType?: string;
  jobSubtype?: string;
  slurryDensity?: number;
  targetLabVolume?: number;
  equivalentSackWeight?: number;
  cementSackWeight?: number;
  comments?: string;
  isBlendTest: boolean;
  pipeSize?: string;
  rigName?: string;
  wellName?: string;
  projectReceivedBy?: string;
  sentToLabDate?: string | null;
  preferredBlendCisPROContainerID?: string;
  preferredBlendCisPROContainerLocation?: string;
  waterFraction?: number;
  viscosifier?: number;
  mudWeight?: number;
  sentToLabBy?: string;
  /** Material composition (dry + liquid), enriched for editing. */
  materials?: SpacerDetailMaterialDto[];
  /** Redesign reason data if this spacer is a redesign */
  redesignReason?: SlurryRedesignReasonDto | null;
}

export interface SpacerMixabilityDto {
  id?: number;
  slurryId?: number;
  score: number | null;
  timeToWetSolids: number | null;
  notes: string | null;
}

export interface SpacerDetailMaterialDto {
  materialId: number;
  materialName?: string;
  materialTypeId: number;
  /** CementBase | PozBase | BlendAdditive | Additive | LiquidBase | LiquidAdditive */
  sectionType?: string;
  basePercent?: number;
  absoluteVolume?: number;
  density?: number;
  weight?: number;
  volume?: number;
  calculatedLabWeight?: number;
  specificGravity?: number;
  overRideSp?: number;
  sackWeight?: number;
  pozSackWeight?: number;
  byWeightOfTypeId: number;
  afterBlend: boolean;
  preferredCisProContainerId?: string;
  preferredCisProContainerLocation?: string;
  containerLocation?: string;
}

export interface SpacerBaseMaterialDto {
  id: number;
  name?: string;
  functionName?: string;
  materialTypes: number;
  absoluteVolume?: number;
  specificGravity?: number;
  sackWeight?: number;
  addAfterStirring: boolean;
  isActive: boolean;
  isFluidLossTestWithFoamNeeded: boolean;
  sampleRefNum?: string;
  recvAmount?: number;
  currentAmount?: number;
  minAmount?: number;
  rigName?: string;
  recvDate?: string | null;
  notifyAtReorder?: boolean;
  trackSample?: boolean;
}

export interface SpacerBriefDto {
  slurryID: number;
  ufSlurryID: string;
  projectName?: string;
  createDate: string;
  sentToLabDate?: string | null;
  createdBy?: string;
  status?: string;
  comments?: string;
  type: number;
}

// ── Spacer Test Input DTOs ───────────────────────────────────────────────────

export interface BatchTestsBriefDto {
  slurryBatchId: any;
  batchTestId: number;
  batchId: number;
  status: number;
  reviewedBy?: string;
  batchNumber: string;
}

export interface SpacerQaqcTitleValueDto {
  id: number;
  title: string;
  groupId?: number;
  groupName?: string;
}

export interface SpacerQaqcTitleDto {
  id: number;
  cementProjId: number;
  temperature?: number;
  fullTitle: string;
  titleValue: SpacerQaqcTitleValueDto;
}

export interface SlurryTestPropertyDto {
  id: number;
  initialTemperature: number;
  initialPressure: number;
  bhst: number;
  finalTemperature: number;
  finalPressure: number;
  temperatureRampTime: number;
  pressureRampTime: number;
  slurryId: number;
  baseDensity?: number;
  foamDensity?: number;
  updateUser?: string;
  updateDate?: string;
  projectSpacerQaqcReadings: SpacerQaqcTitleDto[];
}

export interface SpacerCompatibilityInputDto {
  attachmentsCount: number;
  testInputMasterId: number;
  notes?: string;
  isMuted: boolean;
  outputs: BatchTestsBriefDto[];
  temperature?: number;
  slurryId?: number;
  ufSlurryId?: string;
  mudCisProContainer?: string;
  mudCisProContainerDeleted: boolean;
  attachments?: any[];
  readings?: SpacerCompatibilityReadingDto[];
}

export interface SpacerCompatibilityReadingDto {
  slurryCompatibilityInputReadingId?: number;
  slurryCompatibilityInputId?: number;
  testInputMasterId?: number;
  spacerPercent?: number;
  isSlurryBased?: boolean;
  spacerCompatibilityRatioType1?: number;
  spacerCompatibilityRatioType2?: number;
}

export interface SpacerQaQcInputDto {
  attachmentsCount: number;
  testInputMasterId: number;
  notes?: string;
  isMuted: boolean;
  outputs: BatchTestsBriefDto[];
  temperature?: number;
  attachments?: any[];
  readings?: SpacerQaqcReadingDto[];
}

export interface SpacerQaqcReadingDto {
  titleId?: number | null;
  title?: string;
}

export interface SpacerSsstInputDto {
  attachmentsCount: number;
  testInputMasterId: number;
  notes?: string;
  isMuted: boolean;
  outputs: BatchTestsBriefDto[];
  temperature?: number;
  mudCisProContainer?: string;
  mudCisProContainerDeleted: boolean;
}

export interface SpacerHpHtRheologyInputDto {
  attachmentsCount: number;
  hpHtRheologyTypeId?: number;
  testInputMasterId?: number;

  readings?: {
    temperature?: number;
    pressure?: number;
    time?: number;
  }[];

  notes?: string;
  isMuted?: boolean;
  batchNumber: string;
  outputs?: BatchTestsBriefDto[];
}

export interface SpacerTestInputDto {
  sharedSettings?: SlurryTestPropertyDto;
  spacerCompatibility?: SpacerCompatibilityInputDto;
  spacerQaQc?: SpacerQaQcInputDto;
  spacerSsst?: SpacerSsstInputDto;
  spacerHpHtRheologyInputs: SpacerHpHtRheologyInputDto[];
}

export interface AppendNoteResponseDto {
  success: boolean;
  message: string;
  updatedNotes?: string;
}
export interface SpacerTestAttachmentDto {
  slurryId: number;
  slurryName: string;
  testType: string;

  id: number;
  fileName: string;
  description: string;
  uploadedBy: string;
  ownerId: number;
}

export interface SpacerBatchDto {
  slurryBatchId: number;
  batchNumber: string;
  startBy: string;
  notes: string;
  slurryId: number;
  targetLabVolume?: number;
  batchLabVolume?: number;
  foamDensity?: number;
  measuredBlendWeight?: number;
  measurementTolerance?: number;
  excessFoamPercent?: number;

  capturedBlendCisProcontainerId?: string;

  measuredMudContainer?: string;
  mudContainerComments?: string;
  preferredMudContainer?: string;
  replacePreferredMudContainer?: boolean;

  preferredBlendContainer?: string;
  replacePreferredBlendContainer?: boolean;
  blendContainerComments?: string;
  blendWeightComments?: string;

  selectedFoamerMaterialIds: number[];
  updateDate?: string;

  tests: BatchTestBriefDto[];
  materialOptions: any[];
}

export interface BatchTestBriefDto {
  slurryBatchId: number;
  slurryId: number;
  batchNumber: string;
  testInputMasterId: number;
  testOutputMasterId: number;
  status: number;
  updateUser: string;
  reviewedBy: string;
}

export interface SpacerTestsDto {
  sharedSettings?: any;
  spacerCompatibility?: SpacerCompatibilityWithReadingsDto;
  spacerQaQc?: SpacerQaQcWithOutputsDto;
  spacerSsst?: SpacerSsstWithOutputsDto;
  spacerHpHtRheologyInputs: SpacerHpHtRheologyWithReadingsDto[];
}

export interface SpacerCompatibilityWithReadingsDto {
  testInputMasterId: number;
  temperature?: number;
  slurryId?: number;
  mudCisProContainer?: string;
  mudCisProContainerDeleted: boolean;
  compatibilitySlurry?: number;
  readings: any[];
  outputs: BatchTestBriefDto[];
}

export interface SpacerQaQcWithOutputsDto {
  testInputMasterId: number;
  temperature?: number;
  outputs: BatchTestBriefDto[];
}

export interface SpacerSsstWithOutputsDto {
  testInputMasterId: number;
  temperature?: number;
  mudCisProContainer?: string;
  mudCisProContainerDeleted: boolean;
  outputs: BatchTestBriefDto[];
}

export interface SpacerHpHtRheologyWithReadingsDto {
  testInputMasterId: number;
  hpHtRheologyTypeId?: number;
  readings: any[];
  outputs: BatchTestBriefDto[];
}

export interface UpdateSpacerMaterialDto {
  materialId: number;
  materialName?: string;
  materialTypeId?: number;
  sectionType?: string;
  basePercent?: number;
  absoluteVolume?: number;
  density?: number;
  weight?: number;
  volume?: number;
  calculatedLabWeight?: number;
  specificGravity?: number;
  overRideSp?: number;
  sackWeight?: number;
  pozSackWeight?: number;
  afterBlend: boolean;
  byWeightOfTypeId: number;
  preferredCisProContainerId?: string;
  preferredCisProContainerLocation?: string;
  containerLocation?: string;
}

export interface UpdateSpacerRequest {
  id: number;
  cementProjID?: number;
  ufSlurryID?: string;
  createdBy?: string;
  status: number;
  type: number;
  targetThickeningTimeFrMins?: number;
  targetThickeningTimeToMins?: number;
  measuredDepth?: number;
  slurryDensity?: number;
  targetLabVolume?: number;
  equivalentSackWeight?: number;
  cementSackWeight?: number;
  waterFraction?: number;
  viscosifier?: number;
  mudWeight?: number;
  comments?: string;
  jobType?: string;
  jobSubtype?: string;
  pipeSize?: string;
  rigName?: string;
  wellName?: string;
  projectReceivedBy?: string;
  sentToLabBy?: string;
  sentToLabDate?: string | null;
  isBlendTest: boolean;
  preferredBlendCisPROContainerID?: string;
  preferredBlendCisPROContainerLocation?: string;
  preferredBlendContainer?: string;
  replacePreferredBlendContainer?: boolean;
  blendContainerLocation?: string;
  preferredCisPROContainerIDsHaveDeleted?: boolean;
  spacerTests?: SpacerTestInputDto;
  materials: UpdateSpacerMaterialDto[];
}

export interface CreateSpacerBatchRequest {
  notes?: string;
  batchLabVolume?: number;
  testInputMasterIds: number[];
  measuredMudContainer?: string;
  mudContainerComments?: string;
  preferredMudContainer?: string;
  replacePreferredMudContainer?: boolean;
}

export interface SpacerLabSheetDto {
  projectName: string;
  slurryName: string;
  isBlendTest?: boolean;
  projectBy: string;
  rigName: string;
  wellName: string;
  pipeSize?: string;
  jobType: string;
  density: number;
  mudWeight?: number;
  sentToLab?: string;
  dryMaterials: LabSheetMaterialDto[];
  liquidMaterials: LabSheetMaterialDto[];
  mixabilityScore?: string;
  timeToWetSolids?: number;
  mixabilityNotes?: string;
  notes?: string;
  spacerCompatibility?: SpacerCompatibilityLabSheetDto;
  spacerQaQc?: SpacerQaQcLabSheetDto;
  spacerSsst?: SpacerSsstLabSheetDto;
  spacerHpHtRheology: SpacerHpHtRheologyLabSheetDto[];
}

export interface LabSheetMaterialDto {
  materialName: string;
  percent?: string;
  weight?: number;
  galPerSack?: string;
  preferredContainer?: string;
  containerLocation?: string;
  labWeight?: number;
  isDry: boolean;
}

export interface SpacerCompatibilityLabSheetDto {
  temperature?: number;
  mudContainer?: string;
  mudContainerDeleted: boolean;
  compatibilitySlurryUfid?: string;
  compatibilityProjectName?: string;
  totalWeight?: number;
  notes?: string;
  ratios: SpacerCompatibilityRatioDto[];
}

export interface SpacerCompatibilityRatioDto {
  percentage: number;
  ratioType1?: number;
  ratioType2?: number;
}

export interface SpacerQaQcLabSheetDto {
  temperature?: number;
  notes?: string;
  readingTitles: SpacerQaQcReadingTitleDto[];
}

export interface SpacerQaQcReadingTitleDto {
  title: string;
  groupName?: string;
  temperature?: number;
}

export interface SpacerSsstLabSheetDto {
  temperature?: number;
  notes?: string;
  startedBy?: string;
  stoppedBy?: string;
  conductivityOfPureSpacer?: number;
  startingMudVolume?: number;
  waterDispersion?: string;
  rodCleaningTest?: string;
  comments?: string;
  records: SpacerSsstRecordDto[];
}

export interface SpacerSsstRecordDto {
  spacerAdded?: number;
  spacerPercent?: number;
  conductivity?: number;
  baseline?: number;
}

export interface SpacerHpHtRheologyLabSheetDto {
  equipment: string;
  notes?: string;
  readings: SpacerHpHtReadingDto[];
}

export interface SpacerHpHtReadingDto {
  temperature?: number;
  pressure?: number;
  time?: number;
}

export interface AddTestRequestDto {
  testType: string;
  // SpacerHpHtRheology
  equipmentTypeId?: number;
  readings?: { temperature: number; pressure: number; time: number }[];
  // SpacerCompatibility
  compatibilitySlurryId?: number;
  compatibilityReadings?: {
    spacerPercent: number;
    isSlurryBased?: boolean;
    spacerCompatibilityRatioType1?: number;
    spacerCompatibilityRatioType2?: number;
  }[];
  // SpacerSSST / SpacerCompatibility / SpacerQAQC
  temperature?: number;
  mudCisProContainer?: string;
  // SpacerQAQC
  qaqcReadings?: { titleId: number }[];
  // shared
  notes?: string;
}

export interface UpdateSpacerBatchRequest {
  notes?: string;
  batchLabVolume?: number;
  foamDensity?: number;
  measuredBlendWeight?: number;
  measurementTolerance?: number;
  excessFoamPercent?: number;

  measuredMudContainer?: string;
  preferredMudContainer?: string;
  replacePreferredMudContainer?: boolean;
  mudContainerComments?: string;

  testInputMasterIds: number[];
}

export interface SpacerMeasuredMaterialsDto {
  materials: SpacerBatchMaterialDto[];
  foam?: SpacerBatchFoamDto;
  isMaterialsLocked: boolean;
}

export interface SpacerBatchMaterialDto {
  slurryMaterialId: number;

  materialName: string;

  afterBlend: boolean;

  labWeight?: number;

  measuredWeight?: number;

  preferredContainer?: string;

  measuredContainer?: string;

  isDry: boolean;

  isFoamer: boolean;

  weightComments?: string;

  containerComments?: string;

  replacePreferredContainer?: boolean;
}

export interface SubmitMeasuredMaterialRequest {
  slurryMaterialId: number;
  capturedContainer: string | null;
  replacePreferredContainer: boolean;
  measuredWeight: number | null;
  containerComments: string | null;
  weightComments: string | null;
}


export interface SpacerBatchFoamDto {
  measuredWeight?: number;

  cupWeight?: number;

  cupVolume?: number;

  weightComments?: string;
  
  resultDensity?: number;
}

export interface SubmitMeasuredMaterialsRequestDto {
  materials: SpacerBatchMaterialDto[];
  foam?: SpacerBatchFoamDto;

  measuredBlendWeight?: number;
  capturedBlendContainer?: string;
  blendWeightComments?: string;
  blendContainerComments?: string;

  measuredMudContainer?: string;
  mudContainerComments?: string;
  replacePreferredMudContainer?: boolean;
  replacePreferredBlendContainer?: boolean;
}


export interface CreateSpacerMaterialDto {
  materialId: number;
  materialName: string;
  weight: number;
  absoluteVolume?: number;
  volume?: number;
  labWeight?: number;
  specificGravity?: number;
  materialTypeId: number;
  cisProContainer?: string;
  afterBlend?: boolean;
}

export interface CreateSpacerRequestDto {
  projectId: number;
  labVolume: number;
  totalDensity: number;
  notes?: string;
  waterFractionBased: boolean;
  waterFraction?: number;
  viscosifier?: number;
  isBlendTest: boolean;
  preferredBlendCisPROContainerID?: string;
  materials: CreateSpacerMaterialDto[];
  tests?: any;
}

export interface CreateSpacerResponseDto {
  slurryId: number;
  ufSlurryID: string;
  projectId: number;
  createDate: string;
  status: string;
}
 
export enum ProjectStatuses {
  Draft = 1,
  SenttoLab = 2,
  OngoinginLab = 3,
  FinishedinLab = 4,
  Hold = 5,
  Completed = 6,
  Ongoing = 7,
  DraftAgain = 8,
  SentToLabAgain = 9
}
export enum SlurryStatuses {
  Draft = 1,
  ReadytoTest = 2,
  OngoinginLab = 3,
  TestFailed = 4,
  TestCompleted = 5,
  DraftAgain = 6,
  SentToLabAgain = 7
}
export interface UpdateAttachmentDto {
  id: number;
  fileName: string;
  description: string;
  uploadedBy: string;
  ownerId: number;
}

// ── Spacer Test Review DTOs ───────────────────────────────────────────────────

export interface SpacerQaQcOutputReadingDto {
  spacerQaQcTestOutputId: number;
  testOutputMasterId: number;
  title?: string;
  titleId?: number;
  measuredRpm600?: number;
  measuredRpm300?: number;
  measuredRpm200?: number;
  measuredRpm100?: number;
  measuredRpm60?: number;
  measuredRpm30?: number;
  measuredRpm20?: number;
  measuredRpm10?: number;
  measuredRpm6?: number;
  measuredRpm3?: number;
  pv?: number;
  ty?: number;
}

export interface SpacerQaQcOutputDto {
  testOutputMasterId: number;
  testInputMasterId: number;
  temperature?: number;
  pressure?: number;
  comments?: string;
  startedBy?: string;
  stoppedBy?: string;
  equipmentNo?: string;
  startDate?: string;
  stopDate?: string;
  startTime?: string;
  endTime?: string;
  readings: SpacerQaQcOutputReadingDto[];
}

export interface SpacerCompatibilityOutputReadingDto {
  rheologyOutputId: number;
  testOutputMasterId: number;
  isSlurryBased: boolean;
  spacerPercent: number;
  spacerCompatibilityRatioType1?: number;
  spacerCompatibilityRatioType2?: number;
  measuredRpm600?: number;
  measuredRpm300?: number;
  measuredRpm200?: number;
  measuredRpm100?: number;
  measuredRpm60?: number;
  measuredRpm30?: number;
  measuredRpm20?: number;
  measuredRpm10?: number;
  measuredRpm6?: number;
  measuredRpm3?: number;
  springFactor: number;
  calcMeasuredRpm600?: number;
  calcMeasuredRpm300?: number;
  calcMeasuredRpm200?: number;
  calcMeasuredRpm100?: number;
  calcMeasuredRpm60?: number;
  calcMeasuredRpm30?: number;
  calcMeasuredRpm20?: number;
  calcMeasuredRpm10?: number;
  calcMeasuredRpm6?: number;
  calcMeasuredRpm3?: number;
}

export interface SpacerCompatibilityOutputDto {
  testOutputMasterId: number;
  testInputMasterId: number;
  temperature?: number;
  pressure?: number;
  comments?: string;
  startedBy?: string;
  stoppedBy?: string;
  equipmentNo?: string;
  startDate?: string;
  stopDate?: string;
  startTime?: string;
  endTime?: string;
  readings: SpacerCompatibilityOutputReadingDto[];
}

export interface SpacerSsstOutputReadingDto {
  slurrySpacerSsstOutputId: number;
  testOutputMasterId: number;
  spacerAddedMl?: number;
  conductivity?: number;
  spacerPercentage?: number;
  conductivityPercentage?: number;
  baseline?: number;
}

export interface SpacerSsstOutputDto {
  testOutputMasterId: number;
  testInputMasterId: number;
  temperature?: number;
  pressure?: number;
  comments?: string;
  startedBy?: string;
  stoppedBy?: string;
  equipmentNo?: string;
  startDate?: string;
  stopDate?: string;
  startTime?: string;
  endTime?: string;
  conductivityOfPureSpacer?: number;
  startingMudVolume?: number;
  dispersionInWater?: boolean;
  rodCleaning?: boolean;
  readings: SpacerSsstOutputReadingDto[];
}

export interface SpacerHpHtRheologyOutputReadingDto {
  testOutputHpHtRheologyId: number;
  testOutputMasterId: number;
  temperature?: number;
  pressure?: number;
  totalTime?: number;
  springFactor?: number;
  measuredRpm600?: number;
  measuredRpm300?: number;
  measuredRpm200?: number;
  measuredRpm100?: number;
  measuredRpm60?: number;
  measuredRpm30?: number;
  measuredRpm20?: number;
  measuredRpm10?: number;
  measuredRpm6?: number;
  measuredRpm3?: number;
  measured10Sec?: number;
  measured10Min?: number;
  readingType: number; // HpHtRheologyOutputReadingTypes enum
}

export interface SpacerHpHtRheologyOutputDto {
  testOutputMasterId: number;
  testInputMasterId: number;
  temperature?: number;
  pressure?: number;
  comments?: string;
  startedBy?: string;
  stoppedBy?: string;
  equipmentNo?: string;
  startDate?: string;
  stopDate?: string;
  startTime?: string;
  endTime?: string;
  readings: SpacerHpHtRheologyOutputReadingDto[];
}

export interface SpacerTestOutputDto {
  spacerQaQc?: SpacerQaQcOutputDto;
  spacerCompatibility?: SpacerCompatibilityOutputDto;
  spacerSsst?: SpacerSsstOutputDto;
  spacerHpHtRheologyOutputs: SpacerHpHtRheologyOutputDto[];
}

export interface ReviewSpacerTestOutputRequestDto {
  testInputMasterId: number;
  slurryBatchId: number;
}

export interface ReviewSpacerTestOutputResponseDto {
  testInputMasterId: number;
  slurryBatchId: number;
  status: number; // TestOutputStatuses enum
  testOutput?: SpacerTestOutputDto;
}

export interface UpdateSpacerTestOutputRequestDto {
  testInputMasterId: number;
  slurryBatchId: number;
  slurryId: number;
  status: number; // TestOutputStatuses enum
  spacerCompatibility?: any;
  spacerQaQc?: any;
  spacerSsst?: any;
  spacerHpHtRheologyOutputs?: any[];
  testType: TestTypes;
}

export interface UpdateSpacerTestOutputResponseDto {
  success: boolean;
  message?: string;
  testOutputMasterId: number;
  status: number;
}

export interface ChangeSpacerTestStatusRequestDto {
  testInputMasterId: number;
  slurryBatchId: number;
  status: number; // TestOutputStatuses: Approved=3, Rejected=4, NeedRetake=5
}

export interface ChangeSpacerTestStatusResponseDto {
  success: boolean;
  message: string;
  testInputMasterId: number;
  slurryBatchId: number;
  newStatus: number;
}

export interface RevertSpacerTestRequestDto {
  testInputMasterId: number;
  slurryBatchId: number;
}

export interface RevertSpacerTestResponseDto {
  success: boolean;
  message: string;
  testInputMasterId: number;
  slurryBatchId: number;
  newStatus: number;
}

export interface RecordSpacerTestOutputRequestDto {
  slurryId: number;
  testInputMasterId: number;
  testType: TestTypes;
  bhct?: number;
  pressure?: number;
}

export interface RecordSpacerTestOutputResponseDto {
  batches: BatchTestsBriefDto[];
  slurryId: number;
  testInputMasterId: number;
  bhct?: number;
  pressure?: number;
  isBatchesAvailable: boolean;
}

export interface RecordSpacerTestResultRequestDto {
  testInputMasterId: number;
  slurryBatchId: number;
  slurryId: number;
  testType: TestTypes;
  status: number;
  spacerCompatibility?: any;
  spacerQaQc?: any;
  spacerSsst?: any;
  spacerHpHtRheologyOutputs?: any[];
}

export interface RecordSpacerTestResultResponseDto {
  success: boolean;
  message?: string;
  testOutputMasterId: number;
  status: number;
  reviewedBy: string;
  shouldOpenFinalReport: boolean;
}

export interface RemoveSpacerTestResponseDto {
  slurryId: number;
  message: string;
}


@Injectable({ providedIn: 'root' })
export class SpacerService {
  private apiUrl = `${environment.apiUrl}/slurry`;
  private spacerApiUrl = `${environment.apiUrl}/spacer`;

  constructor(private http: HttpClient) { }

  getRedesignReasons(projectTypeId: number): Observable<SpacerRedesignReasonsResponseDto> {
    return this.http.get<SpacerRedesignReasonsResponseDto>(`${this.apiUrl}/redesign-reasons/${projectTypeId}`);
  }

  getById(slurryId: number): Observable<SpacerDetailDto> {
    return this.http.get<SpacerDetailDto>(`${this.apiUrl}/${slurryId}`);
  }

  /** GET /spacer/base-materials — retrieves all active spacer base materials. */
  getBaseMaterials(): Observable<SpacerBaseMaterialDto[]> {
    return this.http.get<SpacerBaseMaterialDto[]>(`${this.spacerApiUrl}/base-materials`);
  }

  /** GET /spacer/well/{projectId} — retrieves all spacers for a well by project ID. */
  getSpacersForWell(projectId: number): Observable<SpacerBriefDto[]> {
    return this.http.get<SpacerBriefDto[]>(`${this.spacerApiUrl}/well/${projectId}`);
  }

  /** POST /spacer/{slurryId}/notes/append — appends a timestamped note. */
  appendNote(slurryId: number, note: string): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.spacerApiUrl}/${slurryId}/notes/append`, { note });
  }

  /** POST /spacer/{slurryId}/notes/clear — removes all notes from the spacer. */
  clearNote(slurryId: number): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.spacerApiUrl}/${slurryId}/notes/clear`, {});
  }
  /**
   * Get spacer test inputs by slurry ID
   * @param slurryId The slurry ID to get test inputs for
   * @returns Observable<SpacerTestInputDto> containing test inputs or null if no content
   */
  getSpacerTestInputs(slurryId: number): Observable<SpacerTestInputDto | null> {
    return this.http.get<SpacerTestInputDto | null>(`${this.spacerApiUrl}/${slurryId}/tests`);
  }

  /**
   * Append a note to a spacer test
   * @param testInputMasterId The test input master ID
   * @param note The note to append
   * @returns Observable<AppendNoteResponseDto>
   */
  appendSpacerTestNote(testInputMasterId: number, note: string): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.spacerApiUrl}/${testInputMasterId}/notes/test-append`, { note });
  }

  /**
   * Get spacer test attachments by test input master ID
   * @param testInputMasterId The test input master ID
   * @returns Observable<SpacerTestAttachmentDto[]>
   */
  getSpacerTestAttachmentsByTestInputMasterId(
    testInputMasterId: number
  ): Observable<SpacerTestAttachmentDto[]> {
    return this.http.get<SpacerTestAttachmentDto[]>(
      `${this.spacerApiUrl}/tests/${testInputMasterId}/attachments`
    );
  }

  /**
   * Download a spacer test attachment
   * @param ownerId The owner ID (batch test ID or similar)
   * @param attachmentId The attachment ID
   * @returns Observable<Blob>
   */
  downloadSpacerTestAttachment(ownerId: number, attachmentId: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/project-details/test-attachment/${ownerId}/${attachmentId}`,
      { responseType: 'blob' }
    );
  }

  /**
   * Clear all notes from a spacer test
   * @param testInputMasterId The test input master ID
   * @returns Observable<AppendNoteResponseDto>
   */
  clearSpacerTestNote(testInputMasterId: number): Observable<AppendNoteResponseDto> {
    return this.http.post<AppendNoteResponseDto>(`${this.spacerApiUrl}/${testInputMasterId}/notes/test-clear`, { note: '' });
  }

  getSpacerMixability(spacerId: number): Observable<SpacerMixabilityDto | null> {
    return this.http.get<SpacerMixabilityDto | null>(`${this.spacerApiUrl}/${spacerId}/mixability`);
  }

  updateSpacerMixability(spacerId: number, dto: SpacerMixabilityDto): Observable<SpacerMixabilityDto> {
    return this.http.put<SpacerMixabilityDto>(`${this.spacerApiUrl}/${spacerId}/mixability`, dto);
  }

  getBatches(slurryId: number): Observable<SpacerBatchDto[]> {
    return this.http.get<SpacerBatchDto[]>(`${this.spacerApiUrl}/${slurryId}/batches`);
  }

  hasBatches(slurryId: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.spacerApiUrl}/${slurryId}/has-batches`);
  }

  getBatchById(batchId: number): Observable<SpacerBatchDto> {
  return this.http.get<SpacerBatchDto>(`${this.spacerApiUrl}/batches/${batchId}`);
  }

  getMeasuredMaterials(batchId: number): Observable<SpacerMeasuredMaterialsDto> {
    return this.http.get<SpacerMeasuredMaterialsDto>(`${this.spacerApiUrl}/batches/${batchId}/materials`);
  }

  submitMeasuredMaterial(
    batchId: number,
    request: SubmitMeasuredMaterialRequest
  ): Observable<SpacerBatchMaterialDto> {
    return this.http.put<SpacerBatchMaterialDto>(
      `${this.spacerApiUrl}/batches/${batchId}/materials/measure`,
      request
    );
  }
  getTestInputs(slurryId: number): Observable<SpacerTestsDto> {
    return this.http.get<SpacerTestsDto>(`${this.spacerApiUrl}/${slurryId}/test-inputs`);
  }

  createBatch(slurryId: number, request: CreateSpacerBatchRequest): Observable<SpacerBatchDto> {
    return this.http.post<SpacerBatchDto>(`${this.spacerApiUrl}/${slurryId}/batches`, request);
  }

  updateBatchTests(batchId: number, request: UpdateSpacerBatchRequest): Observable<SpacerBatchDto> {
    return this.http.put<SpacerBatchDto>(`${this.spacerApiUrl}/batches/${batchId}/tests`, request);
  }

  submitMeasuredMaterials(batchId: number, request: SubmitMeasuredMaterialsRequestDto): Observable<SpacerMeasuredMaterialsDto> {
    return this.http.post<SpacerMeasuredMaterialsDto>(`${this.spacerApiUrl}/batches/${batchId}/materials`, request);
  }


  getLabSheetData(slurryId: number): Observable<SpacerLabSheetDto> {
    return this.http.get<SpacerLabSheetDto>(`${this.spacerApiUrl}/${slurryId}/labsheet`);
  }

  updateRedesignReason(slurryId: number, request: UpdateSpacerRedesignReasonRequestDto): Observable<void> {
    return this.http.put<void>(`${this.spacerApiUrl}/${slurryId}/redesign-reason`, request);
  }

  getByIdWithTestsLoaded(spacerId: number): Observable<any> {
    return this.http.get<any>(
      `${this.spacerApiUrl}/${spacerId}/testloadedspacer`
    );
  }

  addTest(slurryId: number, body: AddTestRequestDto): Observable<any> {
    return this.http.post<any>(`${this.spacerApiUrl}/${slurryId}/add-test`, body);
  }

  updateSpacer(id: number, request: UpdateSpacerRequest): Observable<void> {
    return this.http.put<void>(`${this.spacerApiUrl}/${id}`, request);
  }

  sendSpacerToLab(spacerId: number): Observable<any> {
    return this.http.post<any>(`${this.spacerApiUrl}/${spacerId}/send-to-lab`, {});
  }

  revertSpacerToDraft(spacerId: number): Observable<any> {
    return this.http.post<any>(`${this.spacerApiUrl}/${spacerId}/revert-to-draft`, {});
  }

  completeSpacer(spacerId: number): Observable<any> {
    return this.http.post<any>(`${this.spacerApiUrl}/${spacerId}/complete`, {});
  }
  // ── Spacer Test Review/Update/Status APIs ─────────────────────────────────

  /**
   * Review spacer test output for a specific batch
   * @param request Review request containing testInputMasterId and slurryBatchId
   * @returns Observable<ReviewSpacerTestOutputResponseDto>
   */
  reviewSpacerTestOutput(request: ReviewSpacerTestOutputRequestDto): Observable<ReviewSpacerTestOutputResponseDto> {
    return this.http.post<ReviewSpacerTestOutputResponseDto>(`${this.spacerApiUrl}/tests/review`, request);
  }

  /**
   * Update spacer test output for a specific batch
   * @param request Update request containing test data
   * @returns Observable<UpdateSpacerTestOutputResponseDto>
   */
  updateSpacerTestOutput(request: UpdateSpacerTestOutputRequestDto): Observable<UpdateSpacerTestOutputResponseDto> {
    return this.http.put<UpdateSpacerTestOutputResponseDto>(`${this.spacerApiUrl}/tests/update`, request);
  }

  /**
   * Change spacer test status (Approve/Reject/NeedRetake)
   * @param request Status change request
   * @returns Observable<ChangeSpacerTestStatusResponseDto>
   */
  changeSpacerTestStatus(request: ChangeSpacerTestStatusRequestDto): Observable<ChangeSpacerTestStatusResponseDto> {
    return this.http.post<ChangeSpacerTestStatusResponseDto>(`${this.spacerApiUrl}/tests/change-status`, request);
  }

  /**
   * Revert spacer test from reviewed status back to InProgress
   * @param request Revert request containing testInputMasterId and slurryBatchId
   * @returns Observable<RevertSpacerTestResponseDto>
   */
  revertSpacerTest(request: RevertSpacerTestRequestDto): Observable<RevertSpacerTestResponseDto> {
    return this.http.post<RevertSpacerTestResponseDto>(`${this.spacerApiUrl}/tests/revert`, request);
  }

  /**
   * Record spacer test output - get available batches for recording
   * @param request Record output request containing slurryId and testInputMasterId
   * @returns Observable<RecordSpacerTestOutputResponseDto>
   */
  recordSpacerTestOutput(request: RecordSpacerTestOutputRequestDto): Observable<RecordSpacerTestOutputResponseDto> {
    return this.http.post<RecordSpacerTestOutputResponseDto>(`${this.spacerApiUrl}/tests/record-output`, request);
  }

  recordSpacerTestResult(request: RecordSpacerTestResultRequestDto): Observable<RecordSpacerTestResultResponseDto> {
    return this.http.post<RecordSpacerTestResultResponseDto>(`${this.spacerApiUrl}/tests/record-result`, request);
  }
  /**
   * Upload an attachment for a spacer test
   * @param testInputMasterId The test input master ID
   * @param file The file to upload
   * @param description Optional description for the attachment
   * @returns Observable<boolean>
   */
  uploadSpacerTestAttachment(testInputMasterId: number, file: File, description?: string): Observable<boolean> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    if (description) {
      formData.append('description', description);
    }
    return this.http.post<boolean>(
      `${this.spacerApiUrl}/tests/${testInputMasterId}/attachments`,
      formData
    );
  }

  updateAttachment(dto: UpdateAttachmentDto): Observable<boolean> {
      return this.http.put<boolean>(
        `${this.spacerApiUrl}/attachment/${dto.id}`,
        dto
      );
    }

  /**
   * Delete a spacer test attachment
   * @param attachmentId The attachment ID
   */
  deleteAttachment(attachmentId: number): Observable<unknown> {
    return this.http.delete(`${this.spacerApiUrl}/attachment/${attachmentId}`);
  }

  /**
   * Remove a spacer test
   * @param testInputMasterId The test input master ID to remove
   * @returns Observable<RemoveSpacerTestResponseDto> containing the slurryId and success message
   */
  removeSpacerTest(testInputMasterId: number, testTypeId: TestTypes): Observable<RemoveSpacerTestResponseDto> {
    return this.http.delete<RemoveSpacerTestResponseDto>(`${this.spacerApiUrl}/tests/${testInputMasterId}?testType=${testTypeId}`);
  }

  createSpacer(request: CreateSpacerRequestDto): Observable<CreateSpacerResponseDto> {
  return this.http.post<CreateSpacerResponseDto>(`${this.spacerApiUrl}/create`, request);
}

  getSlurriesForWell(wellId: number): Observable<{ slurryId: number; slurryName: string }[]> {
    return this.http.get<{ slurryId: number; slurryName: string }[]>(
      `${this.apiUrl}/well/${wellId}/slurries`
    );
  }
}
